import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useCallback } from 'react';
import type { ReleasesResponse, ProductUpdate } from './product-updates-types';
import { transformRelease, getUnviewedUpdates } from './product-updates-transform';
import { markAllReleasesAsViewed } from './product-updates-storage';

const RELEASE_NOTES_URL = 'https://docs.customer.io/releases.json';

async function fetchProductUpdates(): Promise<ProductUpdate[]> {
  const response = await fetch(RELEASE_NOTES_URL);

  if (!response.ok) {
    throw new Error(`Failed to fetch releases: ${response.status} ${response.statusText}`);
  }

  const data: ReleasesResponse = await response.json();
  return (data.releases ?? []).map(transformRelease);
}

export function useProductUpdates() {
  const queryClient = useQueryClient();

  const {
    data: updates = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['product-updates'],
    queryFn: fetchProductUpdates,
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const unviewed = useMemo(() => getUnviewedUpdates(updates), [updates]);
  const hasUnviewed = unviewed.length > 0;

  const markAsViewed = useCallback(() => {
    const releaseIds = updates.map((update) => update.id);
    markAllReleasesAsViewed(releaseIds);
    queryClient.invalidateQueries({ queryKey: ['product-updates'] });
  }, [updates, queryClient]);

  return {
    latest: updates,
    hasUnviewed,
    isLoading,
    isError,
    markAsViewed,
  };
}
